import { IRawStyle } from '@fluentui/react';
import { Checkbox } from '@fluentui/react-components';
import { Checkbox as CheckboxV8 } from '@fluentui/react/lib/Checkbox'; //remove this after migration of this component
import { makeStyles, mergeClasses } from '@griffel/react'; //load tools from griffel and not from fluent/mui because that would load the whole chunk
import { DesignSystem, useAppTheme } from 'App/AppDesignSystem';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { CSSProperties } from 'react';

// Create base styles for v9
const useFluentStyles = makeStyles({
  root: {
    height: '28px',
  },
  checkbox: {
    margin: '4px',
  },
  input: {
    width: '28px',
    height: '28px',
  },
});

export interface IPCheckboxCustomStyles {
  root?: CSSProperties;
  label?: CSSProperties;
}

// Define our own interface independed of Fluent / Mui
interface IPCheckboxProps {
  className?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  labelElement?: JSX.Element;
  indeterminate?: boolean;
  defaultIndeterminate?: boolean;
  styles?: IPCheckboxCustomStyles;
  onChange?: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
}

export const IPCheckbox = (props: IPCheckboxProps) => {
  const { system } = useAppTheme(); // Get the current design system
  const stylesFluent = useFluentStyles();

  // Extract optional inline style overrides
  const rootCustomStyle = props.styles?.root;
  const labelCustomStyle = props.styles?.label;

  if (system === DesignSystem.FluentV9) {
    //
    // Using Fluent UI's Checkbox component
    //
    return (
      <Checkbox
        size="large"
        checked={props.indeterminate ? 'mixed' : props.checked}
        defaultChecked={props.defaultIndeterminate ? 'mixed' : props.defaultChecked}
        disabled={props.disabled}
        required={props.required}
        input={{ className: stylesFluent.input }}
        className={mergeClasses(stylesFluent.root, props.className)}
        indicator={{ className: stylesFluent.checkbox }}
        label={{ style: labelCustomStyle, children: props.labelElement || props.label }}
        style={rootCustomStyle}
        onChange={(ev, data) => props.onChange?.(ev, !!data.checked)}
      />
    );
  } else {
    //remove this after migration of this component
    const propsWithoutStyles: Omit<IPCheckboxProps, 'styles'> = { ...props };

    // Convert CSSProperties to IRawStyle for CheckboxV8
    const convertToIRawStyle = (style?: CSSProperties): IRawStyle | undefined => {
      if (!style) return undefined;
      // If already IRawStyle, return as is
      if (typeof style === 'object' && !('MozOsxFontSmoothing' in style)) {
        return style as IRawStyle;
      }

      // Otherwise, shallow copy CSSProperties as IRawStyle
      return { ...(style as IRawStyle) };
    };

    const getLabelElement = () => {
      if (props.labelElement) {
        return () => (
          <IPStack
            onClick={(ev) => {
              if (props.onChange) props.onChange(undefined, !props.checked);
              ev.preventDefault();
              ev.stopPropagation();
            }}
          >
            {props.labelElement}
          </IPStack>
        );
      } else {
        return undefined;
      }
    };

    const v8Styles = props.styles
      ? {
          root: convertToIRawStyle(props.styles.root),
          label: convertToIRawStyle(props.styles.label),
        }
      : undefined;

    return <CheckboxV8 {...propsWithoutStyles} styles={v8Styles} onRenderLabel={getLabelElement()} />;
  }
};
